import React, { useState } from 'react';
import { HelpCircle, Search, ShieldCheck, Zap, MessageSquare, FileText, CheckCircle2 } from 'lucide-react';

export const HelpCenter: React.FC = () => {
  const [search, setSearch] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketCategory, setTicketCategory] = useState('order_issue');
  const [ticketMessage, setTicketMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const articles = [
    {
      title: 'How does the 72-Hour Buyer Protection Guarantee work?',
      cat: 'Security & Warranty',
      summary: 'Every account and key purchase includes automatic escrow holding. If your account credentials are compromised or invalid, our staff will mediate and grant a full refund or replacement.'
    },
    {
      title: 'How to claim and decrypt digital keys instantly',
      cat: 'Orders & Delivery',
      summary: 'Instant delivery keys are stored in encrypted form using AES-GCM-256. Upon server payment verification, the key is unlocked on your order status page.'
    },
    {
      title: 'Duo Queue vs Solo Pilot Boosting safety guidelines',
      cat: 'Boosting & Coaching',
      summary: 'Duo Queue requires no account credential sharing. If selecting Solo Pilot, verified boosters use VPNs matching your physical region.'
    },
    {
      title: 'Payout procedures and verification requirements for sellers',
      cat: 'Seller FAQ',
      summary: 'Sellers can request withdrawals in USDT, Bank Wire, or PayPal Sandbox once funds clear the escrow inspection window.'
    }
  ];

  const handleSubmitTicket = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
      
      {/* Hero Search */}
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <h1 className="text-3xl sm:text-4xl font-black text-brand-lightText dark:text-brand-darkText">
          GameVault Support & Knowledge Base
        </h1>
        <p className="text-xs sm:text-sm text-brand-lightSecText dark:text-brand-darkSecText">
          Browse documentation on buyer warranty, order deliveries, dispute resolution, and vendor onboarding
        </p>

        <div className="relative pt-2">
          <input
            type="text"
            placeholder="Search articles, guides, or troubleshooting..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-2xl text-xs bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
          <Search className="w-4 h-4 text-brand-lightSecText absolute left-3.5 top-5.5" />
        </div>
      </div>

      {/* Popular Help Articles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {articles.map((art, idx) => (
          <div key={idx} className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-brand-blue dark:text-brand-accent">
              {art.cat}
            </span>
            <h3 className="font-bold text-sm text-brand-lightText dark:text-brand-darkText">
              {art.title}
            </h3>
            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
              {art.summary}
            </p>
          </div>
        ))}
      </div>

      {/* Support Ticket Submission Form */}
      <div className="p-8 rounded-3xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder space-y-6">
        <div>
          <h2 className="text-xl font-bold text-brand-lightText dark:text-brand-darkText">
            Need Direct Assistance? Open a Support Ticket
          </h2>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Our 24/7 moderation team responds to inquiries within 2 hours
          </p>
        </div>

        {submitted ? (
          <div className="p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-bold text-sm">Support Ticket #TKT-8842 Created</p>
              <p className="mt-0.5">Staff has received your inquiry and will follow up in your Messages inbox shortly.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmitTicket} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold mb-1">Issue Category</label>
                <select
                  value={ticketCategory}
                  onChange={(e) => setTicketCategory(e.target.value)}
                  className="w-full p-2.5 text-xs rounded-xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder"
                >
                  <option value="order_issue">Order / Delivery Issue</option>
                  <option value="payment">Payment & Store Credit</option>
                  <option value="account">Account & Security</option>
                  <option value="report_user">Report Violations</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">Subject</label>
                <input
                  type="text"
                  required
                  placeholder="Summary of your question..."
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  className="w-full p-2.5 text-xs rounded-xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Detailed Message</label>
              <textarea
                required
                rows={4}
                placeholder="Include order numbers, transaction references, or specific screenshots..."
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                className="w-full p-3 text-xs rounded-xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder"
              />
            </div>

            <button
              type="submit"
              className="px-6 py-3 rounded-xl text-xs font-bold text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue transition-all"
            >
              Submit Support Ticket
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
